import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "reducers";
import { ReduxActionTypes } from "constants/ReduxActionConstants";
import { finishUserTourHandler } from "pages/organization/helpers";
import { getCurrentUser } from "selectors/usersSelectors";
import { getDataSources } from "selectors/editorSelectors";
import { Datasource, ServiceType } from "api/DatasourcesApi";

import ChargeBalanceTour from "./ChargeBalanceTour";
import InitialTour from "./InitialTour";
import OpenServiceTour from "./OpenServiceTour";
import HowToCreateServiceTour from "./HowToCreateServiceTour";
import HowToCloneTemplateTour from "./HowToCloneTemplateTour";
import LeftSidebarTour from "./LeftSidebarTour";
import HowToOpenServiceOrTemplate from "./HowToOpenServiceOrTemplate";

const ToursWrapper = (props: any) => {
  const dispatch = useDispatch();
  const { activeTabName } = props;
  const datasources: Datasource[] = useSelector(getDataSources);
  const userTours = useSelector((state: AppState) => state.ui.users.userTours);
  const allUserTours = useSelector(
    (state: AppState) => state.ui.users.allTours,
  );
  const user = useSelector((state: any) => getCurrentUser(state));
  const userModals = useSelector((state: AppState) => state.ui.users.modals);

  const [activeTour, setActiveTour] = useState<any>(null);
  const [isHideChat, setIsHideChat] = useState(false);
  const [gifs, setGifs] = useState<any[]>([]);
  const [firstlogin, setFirstLogin] = useState(true);
  const [pausedTourName, setPausedTourName] = useState<string | null>(null);

  const blockedTabs = ["AI Chatbot", "Admin Panel"];
  const isBlockedTab = blockedTabs.includes(activeTabName);

  useEffect(() => {
    const modals = Object.keys(userModals).map(key => ({
      name: key,
      isOpen: userModals[key].isOpen,
    }));
    const firstLogin = modals.find(
      modal => modal.name === "firstLogin" && modal.isOpen,
    );
    setFirstLogin(!!firstLogin);
  }, [userModals]);

  const updTourState = (tourName: string) => {
    const { automatically, ...tourInfo } = userTours[activeTour.name];
    finishUserTourHandler({ userId: user?.id, tourName, tourInfo }, dispatch);
    if (tourName === "openService") {
      dispatch({
        type: ReduxActionTypes.UPD_USER_TOUR_OPEN_SERVICE,
        payload: { openService: { completed: true } },
      });
    }
  };

  useEffect(() => {
    setIsHideChat(!!activeTour);
  }, [activeTour]);

  useEffect(() => {
    const intercomContainer = document.querySelector(
      "body .intercom-lightweight-app",
    );
    if (intercomContainer) {
      intercomContainer.style.display = isHideChat ? "none" : "block";
    }
  }, [isHideChat]);

  function getAllDatasources(data: any[]): any[] {
    const result: any[] = [];
    for (const obj of data) {
      if (obj.hasOwnProperty("datasources")) {
        result.push(...obj.datasources);
      }
      if (obj.isFolder && obj.children) {
        result.push(...getAllDatasources(obj.children));
      }
    }
    return result;
  }

  const datasourceList = getAllDatasources(datasources);

  const findFirstServiceInDS = (): ServiceType | undefined => {
    const firstServiceType = datasourceList.find(obj =>
      obj.serviceActions.includes("OPEN_LAYOUT_TAB"),
    )?.type;
    return firstServiceType;
  };

  useEffect(() => {
    if (!allUserTours?.openService && datasourceList.length > 0) {
      const firstServiceType = findFirstServiceInDS();
      if (firstServiceType) {
        dispatch({
          type: ReduxActionTypes.UPD_USER_TOUR,
          payload: {
            openService: {
              isOpen: true,
              label: "How to open service",
            },
          },
        });
      }
    } else {
      dispatch({
        type: ReduxActionTypes.UPD_USER_TOUR,
        payload: {
          openService: {
            isOpen: false,
            label: "How to open service",
          },
        },
      });
    }
  }, [datasources]);

  const handleFinishTour = () => {
    if (activeTour.automatically) {
      const { automatically, ...tourInfo } = userTours[activeTour.name];
      dispatch({
        type: ReduxActionTypes.UPD_USER_TOUR,
        payload: {
          [activeTour.name]: {
            ...tourInfo,
            isOpen: false,
          },
        },
      });
    } else {
      updTourState(activeTour.name);
    }
  };

  const handleStartTemplateOrServiceTour = () => {
    dispatch({
      type: ReduxActionTypes.UPD_USER_TOUR,
      payload: {
        howToOpenServiceOrTemplate: {
          isOpen: true,
        },
      },
    });
  };

  const tours = [
    {
      name: "initialTour",
      component: InitialTour,
      props: {
        handleOpenDemoTemplate: props.handleOpenDemoTemplate,
        handleLeftMenu: props.handleLeftMenu,
        handleStartTemplateOrServiceTour,
        tourtemplate: props.templatedemo,
      },
    },
    {
      name: "howToOpenServiceOrTemplate",
      component: HowToOpenServiceOrTemplate,
      props: { openLeftMenu: props.openLeftMenu },
    },
    { name: "openService", component: OpenServiceTour },
    { name: "chargeBalance", component: ChargeBalanceTour },
    { name: "howToCreateService", component: HowToCreateServiceTour },
    { name: "howToCloneTemplate", component: HowToCloneTemplateTour },
    { name: "leftSidebar", component: LeftSidebarTour, props: { gifs } },
  ];

  useEffect(() => {
    const gifUrls = [
      "https://ubos.tech/wp-content/uploads/2024/01/home1.gif",
      "https://ubos.tech/wp-content/uploads/2024/01/show-hide.gif",
      "https://ubos.tech/wp-content/uploads/2024/04/template-gif.gif",
      "https://ubos.tech/wp-content/uploads/2024/01/workspace-setting-1.gif",
      "https://ubos.tech/wp-content/uploads/2024/01/logs1.gif",
      "https://ubos.tech/wp-content/uploads/2024/01/user-setting.gif",
    ];
    async function loadGifs() {
      try {
        const gifPromises = gifUrls.map(async url => {
          const response = await fetch(url);
          const data = await response.blob();
          return URL.createObjectURL(data);
        });
        const gifUrlsArray = await Promise.all(gifPromises);
        setGifs(gifUrlsArray);
      } catch (error) {
        console.error("Error loading gifs:", error);
      }
    }
    loadGifs();
  }, []);

  useEffect(() => {
    const toursList = Object.keys(userTours).map(key => ({
      name: key,
      ...userTours[key],
    }));
    const firstOpenTour = toursList.find(tour => tour.isOpen);

    if (isBlockedTab) {
      if (activeTour?.name && userTours[activeTour.name]?.isOpen) {
        setPausedTourName(activeTour.name);
        dispatch({
          type: ReduxActionTypes.UPD_USER_TOUR,
          payload: {
            [activeTour.name]: {
              ...userTours[activeTour.name],
              isOpen: false,
            },
          },
        });
        setActiveTour(null);
      }
    } else {
      if (
        pausedTourName &&
        userTours[pausedTourName] &&
        !userTours[pausedTourName].isOpen
      ) {
        dispatch({
          type: ReduxActionTypes.UPD_USER_TOUR,
          payload: {
            [pausedTourName]: {
              ...userTours[pausedTourName],
              isOpen: true,
            },
          },
        });
        setPausedTourName(null);
      }
      if (JSON.stringify(activeTour) !== JSON.stringify(firstOpenTour)) {
        setActiveTour(firstOpenTour || null);
      }
    }
  }, [
    userTours,
    activeTabName,
    isBlockedTab,
    activeTour,
    dispatch,
    pausedTourName,
  ]);

  return (
    <>
      {!firstlogin && activeTour && activeTour.name && (
        <>
          {tours.map(tour => {
            if (activeTour.name === tour.name) {
              const TourComponent = tour.component as any;
              return (
                <TourComponent
                  key={tour.name}
                  handleFinishTour={handleFinishTour}
                  additionalProps={tour?.props}
                />
              );
            }
            return null;
          })}
        </>
      )}
    </>
  );
};

export default ToursWrapper;
